import React, { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { api, getErrorMessage } from '../lib/api';
import {
  Mail,
  Lock,
  KeyRound,
  CheckCircle2,
  ArrowLeft,
  ArrowRight,
  Eye,
  EyeOff,
  Loader2,
  Sparkles,
} from 'lucide-react';

export const PasswordReset: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const [email, setEmail] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [error, setError] = useState('');

  const requestResetMutation = useMutation({
    mutationFn: async (userEmail: string) => {
      const response = await api.post('/auth/password-reset', { email: userEmail });
      return response.data;
    },
  });

  const confirmResetMutation = useMutation({
    mutationFn: async (data: { token: string; password: string }) => {
      const response = await api.put(`/auth/password-reset/${data.token}`, {
        password: data.password,
      });
      return response.data;
    },
    onSuccess: () => {
      navigate('/login');
    },
  });

  const handleRequestReset = (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    requestResetMutation.mutate(email);
  };

  const handleConfirmReset = (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (newPassword.length < 8) {
      setError('A palavra-passe deve conter pelo menos 8 caracteres');
      return;
    }

    if (newPassword !== confirmPassword) {
      setError('As palavras-passe não coincidem');
      return;
    }

    if (token) {
      confirmResetMutation.mutate({ token, password: newPassword });
    }
  };

  // Request reset form
  if (!token) {
    return (
      <div className="min-h-screen flex bg-slate-50 dark:bg-slate-950 font-sans text-slate-900 dark:text-slate-50">
        {/* Left side - Decorative */}
        <div className="hidden lg:flex lg:w-1/2 relative bg-slate-900 overflow-hidden items-center justify-center">
          <div className="absolute inset-0 z-0 flex items-center justify-center">
            <div className="absolute top-1/4 -left-10 w-96 h-96 bg-primary-500 rounded-full mix-blend-screen filter blur-[100px] opacity-70 animate-blob" />
            <div className="absolute top-1/4 right-10 w-96 h-96 bg-secondary-500 rounded-full mix-blend-screen filter blur-[100px] opacity-70 animate-blob animation-delay-2000" />
            <div className="absolute -bottom-8 left-1/4 w-96 h-96 bg-pink-500 rounded-full mix-blend-screen filter blur-[100px] opacity-70 animate-blob animation-delay-4000" />
            <div className="absolute inset-0 bg-slate-900/60 backdrop-blur-[2px] z-10" />
          </div>

          <div className="relative z-20 p-12 max-w-lg text-center flex flex-col items-center">
            <div className="w-16 h-16 bg-gradient-to-br from-primary-400 to-secondary-500 rounded-2xl mb-8 flex items-center justify-center shadow-xl shadow-primary-500/20">
              <Sparkles className="w-8 h-8 text-white" />
            </div>
            <h1 className="text-4xl font-extrabold text-white tracking-tight sm:text-5xl mb-6 leading-tight">
              Recuperar o seu <br />{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary-400 to-secondary-400">
                Acesso
              </span>
            </h1>
            <p className="text-lg text-slate-300 leading-relaxed">
              Não se preocupe se se esqueceu da sua senha. Vamos ajudá-lo a restabelecer o acesso à
              sua conta de forma segura.
            </p>
          </div>
        </div>

        {/* Right side - Form */}
        <div className="flex-1 flex flex-col justify-center py-12 px-4 sm:px-6 lg:flex-none lg:w-1/2 lg:px-20 xl:px-24">
          <div className="mx-auto w-full max-w-md">
            <div className="text-center lg:text-left mb-8">
              <h2 className="text-3xl font-extrabold tracking-tight text-slate-900 dark:text-white">
                Recuperação de Palavra-passe
              </h2>
              <p className="mt-2 text-sm text-slate-600 dark:text-slate-400">
                Introduza o seu email para enviarmos instruções de reposição.
              </p>
            </div>

            <div className="bg-white dark:bg-slate-900 p-8 sm:p-10 rounded-3xl shadow-xl border border-slate-200/80 dark:border-slate-800">
              {requestResetMutation.isSuccess ? (
                <div className="rounded-2xl bg-emerald-50 dark:bg-emerald-950/30 border border-emerald-200 dark:border-emerald-800/40 p-6 text-center animate-fade-in">
                  <div className="mx-auto flex items-center justify-center h-14 w-14 rounded-2xl bg-emerald-100 dark:bg-emerald-900/60 text-emerald-600 dark:text-emerald-400 mb-4">
                    <CheckCircle2 className="h-7 w-7" />
                  </div>
                  <h3 className="text-lg font-bold text-emerald-900 dark:text-emerald-300 mb-2">
                    Verifique a sua Caixa de Correio
                  </h3>
                  <p className="text-sm text-emerald-800 dark:text-emerald-400 leading-relaxed">
                    Se o email estiver registado, enviámos uma ligação segura com instruções para
                    definir uma nova palavra-passe.
                  </p>
                  <Link
                    to="/login"
                    className="mt-6 w-full flex items-center justify-center gap-2 py-2.5 px-4 rounded-xl border border-slate-300 dark:border-slate-700 text-sm font-semibold text-slate-700 dark:text-slate-200 bg-white dark:bg-slate-800 hover:bg-slate-50 dark:hover:bg-slate-700 transition-all shadow-sm"
                  >
                    <ArrowLeft className="w-4 h-4" /> Voltar ao Início de Sessão
                  </Link>
                </div>
              ) : (
                <form className="space-y-6" onSubmit={handleRequestReset}>
                  <div>
                    <label
                      htmlFor="email"
                      className="block text-xs font-bold uppercase tracking-wider text-slate-700 dark:text-slate-300 mb-1.5"
                    >
                      Endereço de Email
                    </label>
                    <div className="relative">
                      <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                      <input
                        id="email"
                        name="email"
                        type="email"
                        required
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 text-slate-900 dark:text-white placeholder-slate-400 text-sm focus:ring-2 focus:ring-primary-500 outline-none transition-all"
                        placeholder="seu.nome@empresa.com"
                      />
                    </div>
                  </div>

                  {requestResetMutation.isError && (
                    <div className="rounded-xl bg-rose-50 dark:bg-rose-950/40 border border-rose-200 dark:border-rose-900/60 p-4">
                      <p className="text-sm font-medium text-rose-800 dark:text-rose-300">
                        {getErrorMessage(requestResetMutation.error)}
                      </p>
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={requestResetMutation.isPending}
                    className="w-full flex items-center justify-center gap-2 py-3 px-4 rounded-xl shadow-md text-sm font-bold text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-70 transition-all active:scale-[0.98]"
                  >
                    {requestResetMutation.isPending ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" /> A enviar pedido...
                      </>
                    ) : (
                      <>
                        Enviar Link de Recuperação <ArrowRight className="w-4 h-4" />
                      </>
                    )}
                  </button>

                  <div className="text-center pt-2 border-t border-slate-100 dark:border-slate-800">
                    <Link
                      to="/login"
                      className="text-xs font-semibold text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200 inline-flex items-center gap-1.5 transition-colors"
                    >
                      <ArrowLeft className="w-3.5 h-3.5" /> Lembrou-se da senha? Iniciar Sessão
                    </Link>
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Confirm reset form
  return (
    <div className="min-h-screen flex bg-slate-50 dark:bg-slate-950 font-sans text-slate-900 dark:text-slate-50">
      {/* Left side - Decorative */}
      <div className="hidden lg:flex lg:w-1/2 relative bg-slate-900 overflow-hidden items-center justify-center">
        <div className="absolute inset-0 z-0 flex items-center justify-center">
          <div className="absolute top-1/4 -left-10 w-96 h-96 bg-primary-500 rounded-full mix-blend-screen filter blur-[100px] opacity-70 animate-blob" />
          <div className="absolute top-1/4 right-10 w-96 h-96 bg-secondary-500 rounded-full mix-blend-screen filter blur-[100px] opacity-70 animate-blob animation-delay-2000" />
          <div className="absolute -bottom-8 left-1/4 w-96 h-96 bg-pink-500 rounded-full mix-blend-screen filter blur-[100px] opacity-70 animate-blob animation-delay-4000" />
          <div className="absolute inset-0 bg-slate-900/60 backdrop-blur-[2px] z-10" />
        </div>

        <div className="relative z-20 p-12 max-w-lg text-center flex flex-col items-center">
          <div className="w-16 h-16 bg-gradient-to-br from-primary-400 to-secondary-500 rounded-2xl mb-8 flex items-center justify-center shadow-xl shadow-primary-500/20">
            <KeyRound className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-4xl font-extrabold text-white tracking-tight sm:text-5xl mb-6 leading-tight">
            Definir Nova <br />{' '}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary-400 to-secondary-400">
              Palavra-passe
            </span>
          </h1>
          <p className="text-lg text-slate-300 leading-relaxed">
            Escolha uma palavra-passe forte com letras, números e símbolos para proteger a sua conta.
          </p>
        </div>
      </div>

      {/* Right side - Form */}
      <div className="flex-1 flex flex-col justify-center py-12 px-4 sm:px-6 lg:flex-none lg:w-1/2 lg:px-20 xl:px-24">
        <div className="mx-auto w-full max-w-md">
          <div className="text-center lg:text-left mb-8">
            <h2 className="text-3xl font-extrabold tracking-tight text-slate-900 dark:text-white">
              Criar Nova Palavra-passe
            </h2>
            <p className="mt-2 text-sm text-slate-600 dark:text-slate-400">
              Introduza a nova palavra-passe pretendida abaixo.
            </p>
          </div>

          <div className="bg-white dark:bg-slate-900 p-8 sm:p-10 rounded-3xl shadow-xl border border-slate-200/80 dark:border-slate-800">
            <form className="space-y-5" onSubmit={handleConfirmReset}>
              <div className="space-y-4">
                <div>
                  <label
                    htmlFor="newPassword"
                    className="block text-xs font-bold uppercase tracking-wider text-slate-700 dark:text-slate-300 mb-1.5"
                  >
                    Nova Palavra-passe
                  </label>
                  <div className="relative">
                    <Lock className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <input
                      id="newPassword"
                      name="newPassword"
                      type={showNewPassword ? 'text' : 'password'}
                      required
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      className="w-full pl-10 pr-10 py-2.5 rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 text-slate-900 dark:text-white placeholder-slate-400 text-sm focus:ring-2 focus:ring-primary-500 outline-none transition-all"
                      placeholder="••••••••••••"
                    />
                    <button
                      type="button"
                      onClick={() => setShowNewPassword(!showNewPassword)}
                      className="absolute right-3.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 p-0.5"
                    >
                      {showNewPassword ? (
                        <EyeOff className="w-4 h-4" />
                      ) : (
                        <Eye className="w-4 h-4" />
                      )}
                    </button>
                  </div>
                </div>

                <div>
                  <label
                    htmlFor="confirmPassword"
                    className="block text-xs font-bold uppercase tracking-wider text-slate-700 dark:text-slate-300 mb-1.5"
                  >
                    Confirmar Palavra-passe
                  </label>
                  <div className="relative">
                    <Lock className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <input
                      id="confirmPassword"
                      name="confirmPassword"
                      type={showConfirmPassword ? 'text' : 'password'}
                      required
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      className="w-full pl-10 pr-10 py-2.5 rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 text-slate-900 dark:text-white placeholder-slate-400 text-sm focus:ring-2 focus:ring-primary-500 outline-none transition-all"
                      placeholder="••••••••••••"
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      className="absolute right-3.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 p-0.5"
                    >
                      {showConfirmPassword ? (
                        <EyeOff className="w-4 h-4" />
                      ) : (
                        <Eye className="w-4 h-4" />
                      )}
                    </button>
                  </div>
                </div>
              </div>

              {(error || confirmResetMutation.isError) && (
                <div className="rounded-xl bg-rose-50 dark:bg-rose-950/40 border border-rose-200 dark:border-rose-900/60 p-4">
                  <p className="text-sm font-medium text-rose-800 dark:text-rose-300">
                    {error || getErrorMessage(confirmResetMutation.error)}
                  </p>
                </div>
              )}

              <button
                type="submit"
                disabled={confirmResetMutation.isPending}
                className="w-full flex items-center justify-center gap-2 py-3 px-4 rounded-xl shadow-md text-sm font-bold text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-70 transition-all active:scale-[0.98] mt-6"
              >
                {confirmResetMutation.isPending ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" /> A redefinir palavra-passe...
                  </>
                ) : (
                  <>
                    Redefinir Palavra-passe <ArrowRight className="w-4 h-4" />
                  </>
                )}
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};
